import React, { useEffect } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Typography,
  Divider,
  Skeleton,
  Button,
  Tooltip,
} from "@mui/material";
import { FlagFill, MoreHorizontal, User, Refresh02 } from "@tiny/icons";
import { Handle, NodeProps, Position } from "reactflow";
import { useDrawerInvokers } from "@tiny/lib";
import { NodeData, TriggerNodeStats } from "../types";
import { MenuIconButton } from "../../../../components";
import TriggerForm from "../../../../customComponents/TriggerForm";
import { useWorkflowsBuilderPageView } from "../../WorkflowsBuilderPage.context";
import { formatNumber } from "@tiny/utils/formatUtils";

const TriggerNode = ({ data, id }: NodeProps<NodeData>) => {
  const { showDrawer } = useDrawerInvokers();
  const { configured } = data.config;
  const {
    viewMode,
    isStatsLoading,
    getWorkflowStatsError,
    refetchWorkflowStats,
    workflowStats,
    showNodeStats,
    workflowData,
  } = useWorkflowsBuilderPageView();

  const nodeStats = workflowStats?.[id] as TriggerNodeStats | undefined;

  useEffect(() => {
    if (!configured) {
      showDrawer({
        children: <TriggerForm triggerId={id} />,
      });
    }
  }, [configured]);

  const eventMapper = (event: "CUSTOMER_ASSIGNED_TO_SEGMENT" | string) => {
    const eventLabels: { [key: string]: string } = {
      CUSTOMER_ASSIGNED_TO_SEGMENT: "User Added from Form Submission",
      CLEANED_CUSTOMER: "Cleaned Customer",
    };
    return (
      eventLabels[event as keyof typeof eventLabels] || event?.replace("_", " ")
    );
  };

  const handleRefreshStats = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    refetchWorkflowStats();
  };

  const renderStatsContent = () => {
    if (getWorkflowStatsError) {
      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="label2" fontSize={12} color="error">
            Failed to load stats
          </Typography>
          <Button
            size="small"
            startIcon={<Refresh02 sx={{ fontSize: 12 }} />}
            onClick={handleRefreshStats}
            sx={{ minWidth: "auto", fontSize: 12 }}
          >
            Reload
          </Button>
        </Box>
      );
    }

    if (isStatsLoading) {
      return (
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Box>
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={30} height={14} />
            </Box>
          </Box>
        </Box>
      );
    }

    const users = formatNumber(nodeStats?.users || 0);

    return (
      <Tooltip
        title={`${users} users have been triggered by this event`}
        componentsProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                padding: 4,
              },
            },
          },
        }}
        arrow
      >
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" gap={1}>
            <User sx={{ fontSize: 16, color: "grey.80" }} />
            <Typography variant="caption" color="grey.100" component="div">
              <strong>Customers Entered</strong>
              <Typography color="grey.80" component="span" ml={1}>
                {users}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          background: configured ? theme.palette.common.white : "transparent",
          border: `1px solid ${theme.palette.grey[80]}`,
          padding: 3,
          paddingBottom: 4,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          width: "400px",
          cursor: "pointer",
        })}
        onClick={(e) => {
          showDrawer({
            children: <TriggerForm triggerId={id} />,
          });
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mr: -1 }}
        >
          <Box display="flex" alignItems="center">
            <FlagFill
              sx={(theme) => ({
                fontSize: 20,
                mr: 1,
                color: theme.palette.primary[125],
              })}
            />
            <Typography
              variant="label1"
              color="grey.80"
              sx={(theme) => ({
                textTransform: "uppercase",
                color: theme.palette.primary[125],
              })}
            >
              {data.label} {"›"}{" "}
              {workflowData?.triggerType && (
                <>
                  {workflowData.triggerType} {"›"}{" "}
                </>
              )}
              {eventMapper(data.event || "")}
            </Typography>
          </Box>
          <MenuIconButton
            icon={<MoreHorizontal />}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              onClick={(e) => {
                showDrawer({
                  children: <TriggerForm triggerId={id} />,
                });
              }}
            >
              {viewMode ? "Preview" : "Edit"}
            </MenuItem>
          </MenuIconButton>
        </Box>
        <Box mt={2}>
          {configured ? (
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: theme.palette.primary[100],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                lineHeight: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label="Configured"
            />
          ) : (
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: theme.palette.yellow[150],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                lineHeight: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label="Not-Configured"
            />
          )}
        </Box>
        {configured && showNodeStats && (
          <>
            <Divider sx={{ mt: 4 }} />
            <Box mt={2}>{renderStatsContent()}</Box>
          </>
        )}
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ backgroundColor: "transparent", border: "none" }}
      />
    </>
  );
};

export default TriggerNode;
